import {Component, AfterViewInit, OnInit, ViewChild} from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import {MatPaginator} from '@angular/material/paginator';
import {MatTableDataSource} from '@angular/material/table';
import { LoaderService } from '../../services/loader.service';
import { PaymentService } from '../../services/payment.service';
import Swal from 'sweetalert2';

export interface WithdrawRequest {
  amount: number;
  account: string;
  userEmail: string;
  date: { seconds: number, nanoseconds: number },
  updatedAt?: { seconds: number, nanoseconds: number}
  status: string,
  userId: string,
  withdrawId: string
}

@Component({
  selector: 'app-withdrawal-requests',
  templateUrl: './withdrawal-requests.component.html',
  styleUrls: ['./withdrawal-requests.component.css']
})

export class WithdrawalRequestsComponent implements OnInit {
  displayedColumns: string[] = ['position', 'name', 'weight', 'symbol','status', 'action'];
  dataSource: WithdrawRequest[] = [];
  originalDataSource: WithdrawRequest[] = [];
  sortColumn: 'amount' | 'date' = 'date';
  sortDirection: 'asc' | 'desc' = 'desc';
  selectedStatus: string = 'pending';
  
  // Filtros mejorados
  filterEmail: string = '';
  filterMinAmount: number | null = null;
  filterMaxAmount: number | null = null;
  filterStartDate: string = '';
  filterEndDate: string = '';
  
  constructor(
    private afs: AngularFirestore,
    private loaderService: LoaderService,
    private paymentService: PaymentService
  ) { }

  ngOnInit(): void {
    this.getWithdrawalRequests();
  }

  approve(element: any) {
    const data = {
      withdrawId: element.withdrawId,
      approved: true,
    }
    this.paymentService.claimDividends(data)
    console.log('Aprobado:', element);
  }

  changeStatus(withdrawId: string, approved: boolean) {
    this.loaderService.showSpinner('Actualizando estado de la solicitud');
    this.paymentService.changeWithdrawalStatus({withdrawId, approved}).subscribe(
      (res: any) => {
        console.log(res);
        if (approved) {
          Swal.fire('Solicitud aprobada', 'La solicitud de retiro ha sido aprobada', 'success');
          this.dataSource = this.dataSource.filter((request: WithdrawRequest) => request.withdrawId !== withdrawId);
        } else {
          Swal.fire('Solicitud denegada', 'La solicitud de retiro ha sido denegada', 'success');
        }
        this.loaderService.hideSpinner();
        this.getWithdrawalRequests(this.selectedStatus);
      }, (error: any) => {
        console.log(error);
        Swal.fire('Error', 'Ocurrió un error al actualizar el estado de la solicitud', 'error');
        this.loaderService.hideSpinner();
      }
    )
  }

  filterByStatus() {
    this.getWithdrawalRequests(this.selectedStatus);
  }

  getWithdrawalRequests(status: string = 'pending') {
    this.loaderService.showSpinner('Cargando solicitudes de retiro');
    this.afs.collection('withdraws-mangopay').get().subscribe(
      (res: any) => {
        this.originalDataSource = res.docs
          .map((doc: any) => doc.data() as WithdrawRequest)
          .filter((request: WithdrawRequest) => request.status === status);
        
        // Debug: verificar que se cargaron los datos
        console.log('Datos de retiros cargados:', this.originalDataSource.length, 'solicitudes');
        
        this.dataSource = [...this.originalDataSource];
        this.applyFilters();
        this.loaderService.hideSpinner();
      }, 
      (error: any) => {
        console.log(error);
        Swal.fire('Error', 'Error al cargar las solicitudes de retiro', 'error');
        this.loaderService.hideSpinner();
      }
    );
  }

  applyFilters() {
    let filteredData = [...this.originalDataSource];

    // Filtro por email
    if (this.filterEmail.trim()) {
      filteredData = filteredData.filter(request => 
        request.userEmail.toLowerCase().includes(this.filterEmail.toLowerCase())
      );
    }

    // Filtro por monto mínimo
    if (this.filterMinAmount !== null && this.filterMinAmount > 0) {
      filteredData = filteredData.filter(request => 
        (request.amount / 100) >= this.filterMinAmount!
      );
    }

    // Filtro por monto máximo
    if (this.filterMaxAmount !== null && this.filterMaxAmount > 0) {
      filteredData = filteredData.filter(request => 
        (request.amount / 100) <= this.filterMaxAmount!
      );
    }

    // Filtro por fecha de inicio
    if (this.filterStartDate) {
      const startDate = new Date(this.filterStartDate).getTime() / 1000;
      filteredData = filteredData.filter(request => 
        request.date.seconds >= startDate
      );
    }

    // Filtro por fecha de fin
    if (this.filterEndDate) {
      const endDate = new Date(this.filterEndDate).getTime() / 1000;
      filteredData = filteredData.filter(request => 
        request.date.seconds <= endDate
      );
    }

    this.dataSource = filteredData;
    this.applySorting();
  }

  clearFilters() {
    this.filterEmail = '';
    this.filterMinAmount = null;
    this.filterMaxAmount = null;
    this.filterStartDate = '';
    this.filterEndDate = '';
    this.dataSource = [...this.originalDataSource];
    this.applySorting();
  }

  transformDate(dateInput: any): string {
    try {
      if (!dateInput) {
        return 'Fecha no disponible';
      }
      
      let date: Date;
      
      // Si es un objeto Firebase Timestamp
      if (dateInput && typeof dateInput === 'object') {
        // Verificar si tiene método toDate()
        if (typeof dateInput.toDate === 'function') {
          date = dateInput.toDate();
        }
        // Si tiene propiedades seconds y nanoseconds
        else if (dateInput.seconds) {
          date = new Date(dateInput.seconds * 1000);
        }
        // Si ya es un objeto Date
        else if (dateInput instanceof Date) {
          date = dateInput;
        }
        else {
          return 'Formato fecha desconocido';
        }
      }
      // Si es un número (timestamp en segundos)
      else if (typeof dateInput === 'number') {
        date = new Date(dateInput * 1000);
      }
      // Si es string
      else if (typeof dateInput === 'string') {
        date = new Date(dateInput);
      }
      else {
        return 'Tipo fecha no soportado';
      }
      
      // Verificar si la fecha es válida
      if (isNaN(date.getTime())) {
        return 'Fecha inválida';
      }
      
      // Formatear fecha en español
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
      
    } catch (error) {
      console.error('Error al transformar fecha:', error);
      return 'Error en fecha';
    }
  }

  denyWithdrawal(element: any) {
    console.log('Denegado:', element);
  }

  sortBy(column: 'amount' | 'date'): void {
    if (this.sortColumn === column) {
      // Toggle entre descendente y ascendente
      this.sortDirection = this.sortDirection === 'desc' ? 'asc' : 'desc';
    } else {
      this.sortColumn = column;
      // Por defecto, empezar con descendente (mayor a menor)
      this.sortDirection = 'desc';
    }

    this.applySorting();
  }

  applySorting(): void {
    this.dataSource.sort((a, b) => {
      let valueA: number = 0;
      let valueB: number = 0;

      if (this.sortColumn === 'amount') {
        valueA = a.amount || 0;
        valueB = b.amount || 0;
      } else {
        // Manejo robusto para fechas Firebase Timestamp
        valueA = this.getTimestampValue(a.date);
        valueB = this.getTimestampValue(b.date);
      }

      if (valueA < valueB) return this.sortDirection === 'asc' ? -1 : 1;
      if (valueA > valueB) return this.sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }

  private getTimestampValue(dateObj: any): number {
    try {
      if (!dateObj) return 0;
      
      // Si es un objeto Firebase Timestamp con método toMillis()
      if (typeof dateObj.toMillis === 'function') {
        return dateObj.toMillis();
      }
      
      // Si tiene propiedad seconds
      if (dateObj.seconds) {
        return dateObj.seconds * 1000;
      }
      
      // Si ya es un timestamp numérico
      if (typeof dateObj === 'number') {
        return dateObj;
      }
      
      return 0;
    } catch (error) {
      console.error('Error obteniendo timestamp:', error);
      return 0;
    }
  }

  getSortIcon(column: 'amount' | 'date'): string {
    if (this.sortColumn !== column) return 'bi-arrow-down-up';
    // Flecha arriba = descendente (mayor a menor)
    // Flecha abajo = ascendente (menor a mayor)
    return this.sortDirection === 'desc' ? 'bi-arrow-up' : 'bi-arrow-down';
  }

  // Formatear monto para mostrar
  formatAmount(amount: number): string {
    return (amount / 100).toFixed(2);
  }

  // Obtener clase de estado para estilos
  getStatusClass(status: string): string {
    switch (status) {
      case 'approved': return 'text-success';
      case 'denied': return 'text-danger';
      case 'pending': return 'text-warning';
      default: return 'text-muted';
    }
  }


}
